import { Block } from '@components/Layout';
import { ChevronLeft, ChevronRight } from '@icon/Chevrons';
import { Rating } from '@mui/material';
import { getCurrentLocale } from '@util/locale';
import { useTranslations } from 'next-intl';
import { FC, useRef, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
import {
  AboutSectionHeader,
  AdvisorBio,
  AdvisorBioBlock,
  AdvisorBioBlockData,
  AdvisorBioWrapper,
  CarouselArrow,
  CarouselContainer,
  CarouselSlot,
  ListContainer,
  ReadMore,
  AdvisorBioBlockMediaWrapper,
  TestimonialAuthor,
  TestimonialBlockItem,
  TestimonialRating,
  TestimonialRatingIcon,
  TestimonialTitle,
  TestimonialWrapper,
  Wrapper,
} from './style';

import dynamic from 'next/dynamic';

const Video = dynamic(() => import('@components/Video'), { ssr: false });

const Noop = () => null;

const TestimonialLocales = {
  en: 'en',
  es: 'sp',
  'fr-ca': 'fr',
  fr: 'fr',
};

interface Testimonial {
  id?: string;
  reviewer?: string;
  order?: string;
  rate?: number;
}

export interface AdvisorTestimonialsProps {
  testimonials?: Testimonial[];
  advisorBio?: string;
  testimonialCtaLink?: string;
  characterLimit?: number;
  isTeamBio?: boolean;
  video?: string;
  photo?: string;
}

// For use in Story
export const StoryWrapper = styled.div`
  display: flex;
`;

export const AdvisorTestimonials: FC<AdvisorTestimonialsProps> = ({
  testimonials,
  advisorBio,
  characterLimit = 720,
  isTeamBio,
  video,
  photo,
}) => {
  const t = useTranslations();
  const ref = useRef({
    slickNext: () => {},
    slickPrev: () => {},
  });
  const locale = getCurrentLocale();

  const onPrev = () => {
    ref.current.slickPrev();
  };

  const onNext = () => {
    ref.current.slickNext();
  };

  //there are always three client testimonials

  const [selectedIndex, setSelectedIndex] = useState(0);

  const SliderConfig = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    prevArrow: <Noop />,
    nextArrow: <Noop />,
    slidesToScroll: 1,
    mobileFirst: true,
    autoplay: true,
    autoplaySpeed: 5000,
    beforeChange: (current, next) => {
      setSelectedIndex(next); // Track the selected dot index
    },
    customPaging: (i) => (
      <div
        className={`dot ${selectedIndex === i ? 'selected' : ''}`}
        style={{
          width: '24px',
          height: '6px',
          display: 'flex',
          flexDirection: 'column',
          marginRight: '8px',
          marginLeft: '8px',
          backgroundColor: selectedIndex === i ? '#C80000' : '#D9D9D9',
          borderRadius: '8px',
          margin: '0 4px',
        }}
      />
    ),
  };

  const advisorElement = useRef(null);
  const [showFullText, setShowFullText] = useState(false);

  const toggleShowFullText = () => {
    setShowFullText(!showFullText);
  };
  const showReadMore = advisorBio && advisorBio.length > characterLimit;
  const photoUrl = `${process.env.NEXT_PUBLIC_PERSONNEL_SERVICE_S3_UPLOAD_BUCKET_URL}/${photo}`;
  return (
    ((advisorBio && advisorBio !== 'More information about me.') ||
      (testimonials && testimonials.length > 0)) && (
      <Block background={'secondaryBg'}>
        <AdvisorBio ref={advisorElement}>
          <AdvisorBioWrapper data-testid="advisor-bio-section">
            {advisorBio && advisorBio !== 'More information about me.' && (
              <AdvisorBioBlock>
                <AboutSectionHeader data-testid="advisor-bio">
                  {isTeamBio ? t('About Us') : t('About Me')}
                </AboutSectionHeader>
                <AdvisorBioBlockData
                  data-testid="advisor-bio-desc"
                  dangerouslySetInnerHTML={{
                    __html: showFullText
                      ? advisorBio
                      : `${advisorBio.slice(0, characterLimit)}${showReadMore ? '...' : ''}`,
                  }}
                />
                {showReadMore ? (
                  <ReadMore data-testid="advisor-bio-read-more" onClick={toggleShowFullText}>
                    {showFullText ? t('Read Less') : t('Read More')}
                  </ReadMore>
                ) : null}
              </AdvisorBioBlock>
            )}
          </AdvisorBioWrapper>
          {video && (
            <AdvisorBioWrapper>
              <AdvisorBioBlock>
                <AdvisorBioBlockMediaWrapper>
                  <Video youTubeUrl={video} playing={false} controls={true} video_source />
                </AdvisorBioBlockMediaWrapper>
              </AdvisorBioBlock>
            </AdvisorBioWrapper>
          )}
          {photo && !video && photoUrl && (
            <AdvisorBioWrapper>
              <AdvisorBioBlock>
                <AdvisorBioBlockMediaWrapper>
                  <img src={photoUrl} />
                </AdvisorBioBlockMediaWrapper>
              </AdvisorBioBlock>
            </AdvisorBioWrapper>
          )}
          {testimonials && testimonials.length ? (
            <TestimonialWrapper data-testid="testimonial-section">
              <Wrapper>
                <AboutSectionHeader data-testid="testimonials-header">
                  {t('Testimonials')}
                </AboutSectionHeader>
                {testimonials?.length > 1 ? (
                  <CarouselContainer data-testid="testimonial-carousel-container">
                    <CarouselArrow
                      data-testid="testimonial-arrow-left"
                      onClick={onPrev}
                      $right={true}
                    >
                      <ChevronLeft />
                    </CarouselArrow>
                    <ListContainer ref={ref} as={Slider} {...SliderConfig}>
                      {testimonials &&
                        testimonials.map(function (testimonial, index) {
                          return (
                            <CarouselSlot key={index}>
                              <TestimonialBlockItem data-testid={`testimonial-${index}`}>
                                <TestimonialRating
                                  data-testid={`testimonial-rating-container-${index}`}
                                >
                                  <Rating
                                    key={index}
                                    data-testid={`testimonial-rating-${index}`}
                                    name="read-only"
                                    precision={0.1}
                                    defaultValue={testimonial.rate}
                                    readOnly
                                    sx={TestimonialRatingIcon}
                                  />
                                </TestimonialRating>
                                <TestimonialTitle data-testid={`testimonial-desc-${index}`}>
                                  {testimonial?.[TestimonialLocales[locale]] ??
                                    testimonial?.['en'] ??
                                    testimonial?.['fr'] ??
                                    testimonial?.['es'] ??
                                    ''}
                                </TestimonialTitle>
                                <TestimonialAuthor data-testid={`testimonial-author-${index}`}>
                                  {testimonial.reviewer}
                                </TestimonialAuthor>
                              </TestimonialBlockItem>
                            </CarouselSlot>
                          );
                        })}
                    </ListContainer>
                    <CarouselArrow data-testid="testimonial-arrow-right" onClick={onNext}>
                      <ChevronRight />
                    </CarouselArrow>
                  </CarouselContainer>
                ) : (
                  testimonials.length === 1 && (
                    <TestimonialBlockItem data-testid="testimonial">
                      <TestimonialRating data-testid="testimonial-rating-container">
                        <Rating
                          key={0}
                          data-testid="testimonial-rating"
                          name="read-only"
                          precision={0.1}
                          defaultValue={testimonials[0]!.rate}
                          readOnly
                          sx={TestimonialRatingIcon}
                        />
                      </TestimonialRating>
                      <TestimonialTitle data-testid="testimonial-desc">
                        {testimonials[0]?.[TestimonialLocales[locale]] ??
                          testimonials[0]?.['en'] ??
                          testimonials[0]?.['fr'] ??
                          testimonials[0]?.['es'] ??
                          ''}
                      </TestimonialTitle>
                      <TestimonialAuthor data-testid="testimonial-author">
                        {testimonials[0]!.reviewer}
                      </TestimonialAuthor>
                    </TestimonialBlockItem>
                  )
                )}
              </Wrapper>
            </TestimonialWrapper>
          ) : null}
        </AdvisorBio>
      </Block>
    )
  );
};

export default AdvisorTestimonials;
